<!-- Ported from the Polaris kit: src/product/components/MultiSelectInput.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09): "Choose", "none", "Search", "Nothing with that name.", "{n} chosen" and
     "Clear ({n})" go through i18n (POLARIS.SELECT); values can be numbers (the woot's ids). -->
<script setup>
// Several values out of a list, with a search: a button that says what is chosen ("Open, Pending" or "3 chosen") and opens a list of
// checkboxes. The list stays open while you pick. Clear empties it. Built on the registry popover, command and checkbox.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronDown16 } from '@carbon/icons-vue';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const props = defineProps({
  options: { type: Array, required: true },
  placeholder: { type: String, default: '' }, // default: "Choose"
  disabled: Boolean,
  label: { type: String, default: '' },
});
defineOptions({ inheritAttrs: false });
const model = defineModel({ type: Array, default: () => [] });
const { t } = useI18n();
const items = computed(() =>
  props.options.map(o => (typeof o === 'object' ? o : { value: o, label: o }))
);
const summary = computed(() => {
  const names = model.value.map(
    v => items.value.find(o => o.value === v)?.label ?? v
  );
  return names.length > 2
    ? t('POLARIS.SELECT.N_CHOSEN', { count: names.length })
    : names.join(', ');
});
const toggle = value => {
  model.value = model.value.includes(value)
    ? model.value.filter(x => x !== value)
    : [...model.value, value];
};
const open = ref(false);
// The Command filter matches each item's `value` against the search text. The Clear row's value is a private
// word, not its translated label, so a search for a real option ("Limpeza") does not also bring up "Clear".
const CLEAR_VALUE = '__clear_all_chosen__';
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        v-bind="$attrs"
        type="button"
        variant="outline"
        :disabled="disabled"
        :aria-label="
          label
            ? t('POLARIS.SELECT.LABELED', {
                label,
                value: summary || t('POLARIS.SELECT.NONE'),
              })
            : undefined
        "
        class="w-full justify-between font-normal"
        data-multi-select
        :data-count="model.length"
      >
        <span
          class="truncate"
          :class="model.length ? '' : 'text-polaris-muted-foreground'"
        >
          {{ summary || placeholder || t('POLARIS.SELECT.CHOOSE') }}
        </span>
        <ChevronDown16 class="opacity-60" aria-hidden="true" />
      </Button>
    </PopoverTrigger>
    <PopoverContent
      class="w-[var(--reka-popover-trigger-width)] min-w-56 p-0"
      align="start"
    >
      <Command multiple>
        <CommandInput :placeholder="t('POLARIS.SELECT.SEARCH')" />
        <CommandList>
          <CommandEmpty>{{ t('POLARIS.SELECT.NO_MATCH') }}</CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="o in items"
              :key="o.value"
              :value="o.label"
              @select.prevent="toggle(o.value)"
            >
              <Checkbox
                :model-value="model.includes(o.value)"
                tabindex="-1"
                aria-hidden="true"
                class="pointer-events-none"
              />
              {{ o.label }}
            </CommandItem>
          </CommandGroup>
          <template v-if="model.length">
            <CommandSeparator />
            <CommandGroup>
              <CommandItem :value="CLEAR_VALUE" @select.prevent="model = []">
                {{ t('POLARIS.SELECT.CLEAR', { count: model.length }) }}
              </CommandItem>
            </CommandGroup>
          </template>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
